import React, { useState, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  TextField,
  InputAdornment,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PersonIcon from '@mui/icons-material/Person';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import PeopleIcon from '@mui/icons-material/People';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import SearchIcon from '@mui/icons-material/Search';

const PIN_LEVEL_COLORS = {
  UNKNOWN: '#9e9e9e',
  THREE_PERCENT: '#8d6e63',
  SIX_PERCENT: '#a1887f',
  NINE_PERCENT: '#bcaaa4',
  TWELVE_PERCENT: '#78909c',
  FIFTEEN_PERCENT: '#607d8b',
  EIGHTEEN_PERCENT: '#546e7a',
  TWENTY_ONE_PERCENT: '#455a64',
  TWENTY_FIVE_PERCENT: '#37474f',
  SILVER: '#b0bec5',
  GOLD: '#ffd54f',
  PLATINUM: '#90caf9',
  RUBY: '#ef5350',
  SAPPHIRE: '#42a5f5',
  EMERALD: '#66bb6a',
  DIAMOND: '#ce93d8',
  CROWN_AMBASSADOR: '#ffb74d',
};

const TEAM_COLORS = [
  '#1976d2', '#388e3c', '#f57c00', '#7b1fa2',
  '#c62828', '#00838f', '#4527a0', '#ad1457',
];

function getTeamColor(teamName) {
  if (!teamName) return '#757575';
  let hash = 0;
  for (let i = 0; i < teamName.length; i++) {
    hash = teamName.charCodeAt(i) + ((hash << 5) - hash);
  }
  return TEAM_COLORS[Math.abs(hash) % TEAM_COLORS.length];
}

function buildTree(users) {
  const userMap = new Map();
  users.forEach((u) => userMap.set(u.iboNumber, { ...u, children: [] }));

  const roots = [];
  userMap.forEach((user) => {
    if (user.uplinePlatinumIbo && userMap.has(user.uplinePlatinumIbo)) {
      userMap.get(user.uplinePlatinumIbo).children.push(user);
    } else {
      roots.push(user);
    }
  });

  const sortChildren = (node) => {
    node.children.sort((a, b) => (a.iboName || '').localeCompare(b.iboName || ''));
    node.children.forEach(sortChildren);
  };
  roots.sort((a, b) => (a.iboName || '').localeCompare(b.iboName || ''));
  roots.forEach(sortChildren);

  return roots;
}

function getDescendantCount(node) {
  let count = node.children.length;
  node.children.forEach((c) => { count += getDescendantCount(c); });
  return count;
}

function collectAllIbos(nodes) {
  const ibos = new Set();
  const visit = (node) => {
    ibos.add(node.iboNumber);
    node.children.forEach(visit);
  };
  nodes.forEach(visit);
  return ibos;
}

function findMatchingIbos(nodes, searchTerm) {
  const matches = new Set();
  const s = searchTerm.toLowerCase();
  const visit = (node) => {
    if (
      (node.iboNumber && node.iboNumber.toLowerCase().includes(s)) ||
      (node.iboName && node.iboName.toLowerCase().includes(s)) ||
      (node.email && node.email.toLowerCase().includes(s))
    ) {
      matches.add(node.iboNumber);
    }
    node.children.forEach(visit);
  };
  nodes.forEach(visit);
  return matches;
}

function findAncestors(users, matchingIbos) {
  const userMap = new Map();
  users.forEach((u) => userMap.set(u.iboNumber, u));

  const ancestors = new Set();
  matchingIbos.forEach((ibo) => {
    let current = userMap.get(ibo);
    while (current && current.uplinePlatinumIbo && userMap.has(current.uplinePlatinumIbo)) {
      ancestors.add(current.uplinePlatinumIbo);
      current = userMap.get(current.uplinePlatinumIbo);
    }
  });
  return ancestors;
}

function TreeNode({ node, depth, expanded, onToggle, onContextMenu, searchTerm, matchingIbos, onDragStart, onDragOver, onDrop, dragOverIbo, draggable }) {
  const isExpanded = expanded.has(node.iboNumber);
  const hasChildren = node.children.length > 0;
  const descendantCount = getDescendantCount(node);
  const pinColor = PIN_LEVEL_COLORS[node.pinLevel] || PIN_LEVEL_COLORS.UNKNOWN;
  const teamColor = getTeamColor(node.teamName);
  const isMatch = matchingIbos && matchingIbos.has(node.iboNumber);
  const isDragTarget = draggable && dragOverIbo === node.iboNumber;

  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onContextMenu) onContextMenu(e, node);
  };

  return (
    <>
      <Box
        draggable={draggable}
        onDragStart={draggable ? (e) => {
          e.dataTransfer.setData('text/plain', node.iboNumber);
          e.dataTransfer.effectAllowed = 'move';
          if (onDragStart) onDragStart(node);
        } : undefined}
        onDragOver={draggable ? (e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          if (onDragOver) onDragOver(e, node);
        } : undefined}
        onDragLeave={draggable ? (e) => {
          if (onDragOver) onDragOver(e, null);
        } : undefined}
        onDrop={draggable ? (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (onDrop) onDrop(e, node);
        } : undefined}
        onContextMenu={handleContextMenu}
        onClick={() => hasChildren && onToggle(node.iboNumber)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pl: depth * 3 + 1,
          pr: 2,
          py: 0.75,
          cursor: hasChildren ? 'pointer' : 'default',
          bgcolor: isDragTarget
            ? 'rgba(102, 126, 234, 0.18)'
            : isMatch
              ? 'rgba(102, 126, 234, 0.08)'
              : 'transparent',
          borderLeft: depth > 0
            ? `2px solid ${isDragTarget ? '#667eea' : isMatch ? '#667eea' : 'rgba(0,0,0,0.08)'}`
            : 'none',
          outline: isDragTarget ? '2px dashed #667eea' : 'none',
          outlineOffset: -2,
          '&:hover': {
            bgcolor: isDragTarget
              ? 'rgba(102, 126, 234, 0.22)'
              : isMatch
                ? 'rgba(102, 126, 234, 0.12)'
                : 'rgba(0,0,0,0.04)',
          },
          transition: 'background-color 0.15s, outline 0.15s',
          minHeight: 44,
        }}
      >
        {draggable && (
          <DragIndicatorIcon sx={{ fontSize: 16, color: 'text.disabled', cursor: 'grab', flexShrink: 0 }} />
        )}

        {hasChildren ? (
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onToggle(node.iboNumber); }}
            sx={{ p: 0.25 }}
          >
            {isExpanded ? (
              <ExpandMoreIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            ) : (
              <ChevronRightIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            )}
          </IconButton>
        ) : (
          <Box sx={{ width: 24 }} />
        )}

        <PersonIcon sx={{ fontSize: 18, color: pinColor }} />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
              variant="body2"
              noWrap
              sx={{ fontWeight: 600, fontSize: '0.85rem' }}
            >
              {node.iboName || 'Unknown'}
            </Typography>
            {node.uplineOverride && (
              <Tooltip title="Upline manually overridden (KPI sync disabled)" arrow>
                <SwapVertIcon sx={{ fontSize: 14, color: '#f57c00' }} />
              </Tooltip>
            )}
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            IBO: {node.iboNumber}
            {node.email && ` · ${node.email}`}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          {node.teamName && (
            <Chip
              label={node.teamName}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                bgcolor: teamColor,
                color: '#fff',
                '& .MuiChip-label': { px: 0.8 },
              }}
            />
          )}
          {node.pinLevel && node.pinLevel !== 'UNKNOWN' && (
            <Chip
              label={node.pinLevel.replace(/_/g, ' ')}
              size="small"
              variant="outlined"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                borderColor: pinColor,
                color: pinColor,
                '& .MuiChip-label': { px: 0.8 },
              }}
            />
          )}
          {hasChildren && (
            <Tooltip title={`${descendantCount} downline member${descendantCount !== 1 ? 's' : ''}`} arrow>
              <Chip
                icon={<PeopleIcon sx={{ fontSize: '14px !important' }} />}
                label={descendantCount}
                size="small"
                variant="outlined"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  '& .MuiChip-label': { px: 0.5 },
                  '& .MuiChip-icon': { ml: 0.5 },
                }}
              />
            </Tooltip>
          )}
        </Box>
      </Box>

      {hasChildren && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          {node.children.map((child) => (
            <TreeNode
              key={child.iboNumber}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              onContextMenu={onContextMenu}
              searchTerm={searchTerm}
              matchingIbos={matchingIbos}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDrop={onDrop}
              dragOverIbo={dragOverIbo}
              draggable={draggable}
            />
          ))}
        </Collapse>
      )}
    </>
  );
}

const HierarchyTreeView = ({ users, onContextMenu, onDropReparent, editMode }) => {
  const [expanded, setExpanded] = useState(new Set());
  const [treeSearch, setTreeSearch] = useState('');
  const [dragOverIbo, setDragOverIbo] = useState(null);
  const draggedUserRef = useRef(null);

  const userByIbo = useMemo(() => {
    const map = new Map();
    users.forEach((u) => map.set(u.iboNumber, u));
    return map;
  }, [users]);

  const tree = useMemo(() => buildTree(users), [users]);

  const matchingIbos = useMemo(() => {
    if (!treeSearch.trim()) return null;
    return findMatchingIbos(tree, treeSearch.trim());
  }, [tree, treeSearch]);

  const autoExpanded = useMemo(() => {
    if (!matchingIbos || matchingIbos.size === 0) return null;
    return findAncestors(users, matchingIbos);
  }, [users, matchingIbos]);

  const effectiveExpanded = useMemo(() => {
    if (!autoExpanded) return expanded;
    const merged = new Set(expanded);
    autoExpanded.forEach((ibo) => merged.add(ibo));
    return merged;
  }, [expanded, autoExpanded]);

  const handleToggle = useCallback((iboNumber) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(iboNumber)) {
        next.delete(iboNumber);
      } else {
        next.add(iboNumber);
      }
      return next;
    });
  }, []);

  const handleExpandAll = useCallback(() => {
    setExpanded(collectAllIbos(tree));
  }, [tree]);

  const handleCollapseAll = useCallback(() => {
    setExpanded(new Set());
  }, []);

  const handleDragStart = useCallback((node) => {
    draggedUserRef.current = node;
  }, []);

  const handleDragOver = useCallback((e, node) => {
    if (node) {
      setDragOverIbo(node.iboNumber);
    } else {
      setDragOverIbo(null);
    }
  }, []);

  const handleDrop = useCallback((e, targetNode) => {
    setDragOverIbo(null);
    const draggedIbo = e.dataTransfer.getData('text/plain');
    if (!draggedIbo || draggedIbo === targetNode.iboNumber) return;

    const draggedUser = draggedUserRef.current || userByIbo.get(draggedIbo);
    if (!draggedUser) return;

    if (onDropReparent) {
      onDropReparent(draggedUser, targetNode);
    }
    draggedUserRef.current = null;
  }, [userByIbo, onDropReparent]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50',
        }}
      >
        <TextField
          size="small"
          placeholder="Search in tree..."
          value={treeSearch}
          onChange={(e) => setTreeSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 220, flex: 1, maxWidth: 360 }}
        />
        <Tooltip title="Expand all" arrow>
          <IconButton size="small" onClick={handleExpandAll}>
            <UnfoldMoreIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Collapse all" arrow>
          <IconButton size="small" onClick={handleCollapseAll}>
            <UnfoldLessIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {tree.length} root{tree.length !== 1 ? 's' : ''}
          {matchingIbos && ` · ${matchingIbos.size} match${matchingIbos.size !== 1 ? 'es' : ''}`}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {tree.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No users to display</Typography>
          </Box>
        ) : (
          tree.map((root) => (
            <TreeNode
              key={root.iboNumber}
              node={root}
              depth={0}
              expanded={effectiveExpanded}
              onToggle={handleToggle}
              onContextMenu={onContextMenu}
              searchTerm={treeSearch}
              matchingIbos={matchingIbos}
              onDragStart={editMode ? handleDragStart : undefined}
              onDragOver={editMode ? handleDragOver : undefined}
              onDrop={editMode ? handleDrop : undefined}
              dragOverIbo={editMode ? dragOverIbo : null}
              draggable={!!editMode}
            />
          ))
        )}
      </Box>
    </Box>
  );
};

export default HierarchyTreeView;
